import React from 'react'
import {
  getImgProps,
  Grid,
  Heading,
  VerticalSpacer,
  Button,
  Body,
  CourseInfo,
  Message,
  Flag,
  Link,
  Price,
  TextButton,
  scrollTo,
} from '@superhi/design'
import { useQuery } from '@apollo/client'

import { buildAccountUrl } from '../../../../../../routing/utils'
import { useBasket } from '../../../../../../basket'
import { useIsPreRelease } from '../../../../utils'
import {
  RECURRING_INTERVAL,
  useGetPrice as useGetMembershipPrice,
} from '../../../../../Membership/pages/Product/utils'

import {
  Wrapper,
  Card,
  ImageWrapper,
  Img,
  Info,
  Buttons,
  OneOffMessageWrapper,
  ButtonWrapper,
} from './styles'

import { buildPath } from 'src/routing'
import { ACCOUNT_URL, DASHBOARD_URL } from 'src/config'
import { API } from 'src/api'
import PRICE_QUERY from 'src/components/CourseCard/queries'
import { formatDate } from 'src/components/CourseCard'

const DURATION: Record<API.ProductCourseLength, string> = {
  EIGHT_WEEKS: 'Course',
  FOUR_SIX_WEEKS: 'Course',
  SHORT: 'Workshop',
}

const DASHBOARD_PATH = DASHBOARD_URL
const MEMBERSHIP_PRODUCT_URL = buildPath('membership-product')
const COLLECTION_URL = buildPath('collections-index')
const CART_URL = buildPath('cart')

const MEMBERSHIP_CHECKOUT_URL = buildPath('membership-product-checkout')

const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 'full',
  large: 'full',
}
const BUTTON_FULL_WIDTH = { small: true, medium: true, large: true }

const VS_1_AMOUNT = { small: 12, medium: 12, large: 16 }

const getCourseType = ({
  isBeta,
  isFree,
  isPreRelease,
}: {
  isPreRelease: boolean
  isFree: boolean
  isBeta: boolean
}) => {
  if (isBeta) {
    return `beta`
  } else if (isFree) {
    return `free`
  } else if (isPreRelease) {
    return `pre-release`
  } else {
    return 'normal'
  }
}

type Props = {
  id: string
  slug: string
  title: string
  backgroundImage: API.Course_productCourse['image']
  price: {
    value: number
    currencyCode: API.CurrencyCode
  }
  difficultyLevel: API.ProductCourseDifficultyLevel
  duration: API.ProductCourseLength
  releaseDate: string
  variantId: string
  description: string
  hasAccessToCourse: boolean
  dashboardCourseSlug: string
  isBeta: boolean
  hasFaqs: boolean
  faqsId: string
  betaCoursesId: string
  syllabusId: string
  trailerWistiaId?: string
}

const Hero: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  isBeta,
  title,
  backgroundImage,
  trailerWistiaId,
  price,
  duration,
  variantId,
  releaseDate,
  difficultyLevel,
  description,
  hasAccessToCourse,
  hasFaqs,
  faqsId,
  syllabusId,
  betaCoursesId,
  dashboardCourseSlug,
}) => {
  const basket = useBasket()
  const [addLineItems] = basket.addLineItems
  const membershipPrice = useGetMembershipPrice()

  const { data } = useQuery<API.CourseCardComponent_price, API.CourseCardComponent_priceVariables>(
    PRICE_QUERY,
    {
      variables: {
        id,
      },
      ssr: false,
    },
  )

  const actualPrice = data?.productCourse?.lowestPrice || price

  const isInBasket = variantId ? basket.containsVariant(variantId) : false

  const imageProps = React.useMemo(
    () => ({
      ...getImgProps(
        backgroundImage.url,
        {
          small: 100,
          medium: 100,
          large: 100,
          xlarge: 100,
        },
        {
          fm: 'png8',
        },
      ),
      width: backgroundImage.width,
      height: backgroundImage.height,
    }),
    [backgroundImage],
  )

  const isFree = actualPrice.value === 0

  const isPreRelease = useIsPreRelease(releaseDate)
  const formattedReleaseDate = React.useMemo(
    () => (releaseDate ? formatDate(releaseDate) : undefined),
    [releaseDate],
  )
  const releaseDateText = isPreRelease ? formattedReleaseDate : undefined

  const courseType = getCourseType({
    isPreRelease,
    isBeta,
    isFree,
  })

  let buttonMessage = null
  let buttonProps: React.ComponentProps<typeof Button> = {}
  let extraMessage = null
  let oneOffMessage = null

  const locations: { [path: string]: string } = {
    'generative-art-with-touchdesigner': 'https://www.learngenerative.art/',
    'ai-integration-for-the-web-chatgpt-workshop': 'https://chatgpt-workshop.superhi.com/',
    'better-than-launched': 'https://course.betterthanlaunched.com/',
    'introduction-to-3d-modeling-and-3d-printing':
      'https://intro-to-3d-modeling-workshop.superhi.com/',
    'presenting-persuasively': 'https://learn.presentation.design/',
    'designing-workshops-that-stick-a-guide-for-creatives':
      'https://designing-workshops-that-stick.superhi.com/',
    'intro-to-full-stack-javascript': 'https://intro-to-full-stack-javascript.superhi.com/',
    'the-responsible-technology-toolkit': 'https://responsible-technology.superhi.com/',
    'what-can-ux-ui-folks-learn-from-game-design':
      'https://learn-ux-ui-from-game-design.superhi.com/',
    'how-to-set-up-a-store-with-shopify-2-0': 'https://building-shopify-stores.superhi.com',
  }

  const dashboardCourseUrl =
    locations[dashboardCourseSlug] || `${DASHBOARD_PATH}/${dashboardCourseSlug}`

  switch (courseType) {
    case 'normal': {
      if (hasAccessToCourse) {
        buttonMessage = "You've already got access to this course!"
        buttonProps = {
          children: 'Get started',
          href: dashboardCourseUrl,
        }
      } else {
        buttonMessage = isInBasket ? (
          'This course is in your cart!'
        ) : (
          <>
            <span>Purchase this course for </span>
            <strong>
              <Price currencyCode={actualPrice.currencyCode}>{actualPrice.value}</Price>
            </strong>
          </>
        )
        buttonProps = isInBasket
          ? {
              children: 'Go to cart',
              href: CART_URL,
            }
          : {
              children: 'Add to cart',
              onClick: () => addLineItems([variantId]),
            }

        // oneOffMessage = (
        //   <>
        //     <span>Or...</span>{' '}
        //     <TextButton
        //       onClick={() => addLineItems([variantId])}
        //       color="BLUE_50"
        //       hoverColor="BLUE_60"
        //     >
        //       buy this course
        //     </TextButton>{' '}
        //     <span>on its own for</span>{' '}
        //     <strong>
        //       <Price currencyCode={actualPrice.currencyCode}>{actualPrice.value}</Price>
        //     </strong>
        //     <span>.</span>
        //   </>
        // )

        extraMessage = (
          <>
            <span>Or... get a discount by building a bundle with our </span>
            <Link href={COLLECTION_URL}>learning paths</Link>
            <span>!</span>
          </>
        )
      }

      break
    }
    case 'pre-release': {
      if (hasAccessToCourse) {
        buttonMessage = (
          <>
            <strong>Released on {releaseDateText}</strong>
            <span>. You&rsquo;ll have access to it as soon as it&rsquo;s released.</span>
          </>
        )
        buttonProps = {
          children: 'Get started',
          href: DASHBOARD_URL,
        }
      } else {
        buttonMessage = (
          <>
            <strong>Released on {releaseDateText}</strong>
            <span>. Join</span> <Link href={MEMBERSHIP_PRODUCT_URL}>SuperHi Unlimited </Link>
            <span>now and get access to all our courses</span>{' '}
            {membershipPrice && (
              <>
                <span>for</span>{' '}
                <strong>
                  {membershipPrice?.display}
                  {membershipPrice?.recurring
                    ? `/${RECURRING_INTERVAL[membershipPrice.recurring.interval]}`
                    : ''}
                </strong>{' '}
              </>
            )}
            <span>and access to this course as soon as it’s released!</span>
          </>
        )
        buttonProps = {
          children: 'Get started',
          href: MEMBERSHIP_CHECKOUT_URL,
        }

        oneOffMessage = (
          <>
            <span>Or...</span>{' '}
            <TextButton
              onClick={() => addLineItems([variantId])}
              color="BLUE_50"
              hoverColor="BLUE_60"
            >
              pre-order this course
            </TextButton>{' '}
            <span>on its own for </span>
            <Price currencyCode={price.currencyCode}>{price.value}</Price>
            <span>.</span>
          </>
        )
      }
      break
    }
    case 'beta': {
      if (hasAccessToCourse) {
        buttonMessage = "You've already got access to this course!"
        buttonProps = {
          children: 'Get started',
          href: dashboardCourseUrl,
        }
      } else {
        buttonMessage = (
          <>
            <span>This</span>{' '}
            <TextButton
              color="BLUE_50"
              hoverColor="BLUE_60"
              onClick={() => scrollTo(betaCoursesId)}
            >
              beta course
            </TextButton>{' '}
            <span>is only available as part of</span>{' '}
            <Link href={MEMBERSHIP_PRODUCT_URL}>SuperHi Unlimited</Link>
            <span>. Get access to all our courses</span>{' '}
            {membershipPrice && (
              <>
                <span>for</span>{' '}
                <strong>
                  {membershipPrice?.display}
                  {membershipPrice?.recurring
                    ? `/${RECURRING_INTERVAL[membershipPrice.recurring.interval]}`
                    : ''}
                </strong>{' '}
              </>
            )}
            <span>by joining now!</span>
          </>
        )
        buttonProps = {
          children: 'Get started',
          href: MEMBERSHIP_CHECKOUT_URL,
        }
      }
      break
    }
    case 'free': {
      if (hasAccessToCourse) {
        buttonMessage = "You've already got access to this course!"
        buttonProps = {
          children: 'Get started',
          href: dashboardCourseUrl,
        }
      } else {
        extraMessage = (
          <>
            <span>Or... get a discount on our courses by building a bundle with </span>
            <Link href={COLLECTION_URL}>learning paths</Link>
            <span>!</span>
          </>
        )
        buttonMessage = (
          <>
            <span>Try this </span>
            <strong>free course</strong>
            <span> to learn some of the basics, and get a taster of how we teach!</span>
          </>
        )
        buttonProps = {
          children: 'Sign up for free',
          href: buildAccountUrl(ACCOUNT_URL, 'signup', {
            redirectUrl: dashboardCourseUrl,
          }),
        }
      }
      break
    }
  }

  // if (isInBasket) {
  //   oneOffMessage = 'This course is in your cart!'
  // }

  return (
    <Wrapper data-component-name="Hero">
      <ImageWrapper $backgroundColor="WARM_BLUE_40">
        <Img
          {...imageProps}
          fit="cover"
          alt={`${title} artwork`}
          backgroundColor="WARM_BLUE_50"
          blurHash={backgroundImage.blurhash}
        />
      </ImageWrapper>

      <Grid>
        <Grid.Cell span={GRID_1_CELL_SPAN_1}>
          <Card>
            <VerticalSpacer amount={VS_1_AMOUNT}>
              <Heading level="1" color="BLUE_50">
                {title}
              </Heading>

              <div>
                <Info>
                  <CourseInfo
                    difficultyLevel={difficultyLevel}
                    duration={DURATION[duration]}
                    level="1"
                  />

                  {/* NOTE: Beta courses are part of memberships, so don't
                   show FREE flag */}
                  {!isBeta && isFree && <Flag>Free</Flag>}

                  {isBeta && <Flag>Beta</Flag>}
                </Info>
              </div>

              <Body level="1">{description}</Body>
            </VerticalSpacer>

            <VerticalSpacer amount={{ small: 16, medium: 16, large: 16 }}>
              <Buttons>
                {trailerWistiaId && (
                  <Button
                    version="outline"
                    href={`https://superhi.wistia.com/medias/${trailerWistiaId}`}
                    isExternal
                    fullWidth
                  >
                    Trailer
                  </Button>
                )}
                <Button version="outline" fullWidth onClick={() => scrollTo(syllabusId)}>
                  Syllabus
                </Button>
                {hasFaqs && (
                  <Button version="outline" fullWidth onClick={() => scrollTo(faqsId)}>
                    FAQs
                  </Button>
                )}
              </Buttons>

              <ButtonWrapper>
                {buttonMessage && <Body level="3">{buttonMessage}</Body>}
                <Button {...buttonProps} level="1" fullWidth={BUTTON_FULL_WIDTH} />
              </ButtonWrapper>

              {extraMessage && <Message level="2">{extraMessage}</Message>}

              {oneOffMessage && (
                <OneOffMessageWrapper data-component-name="OneOffMessage">
                  <Body level="3">{oneOffMessage}</Body>
                </OneOffMessageWrapper>
              )}
            </VerticalSpacer>
          </Card>
        </Grid.Cell>
      </Grid>
    </Wrapper>
  )
}

export default Hero
